import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  makeStyles,
  Theme,
} from '@bb-ui/react-library';
import { Warning, Clear } from '@bb-ui/icons/dist/small';
import { SelectedSite } from '../hooks/useCohortCreation';

const useStyles = makeStyles((theme: Theme) => ({
  siteTableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    maxHeight: '300px',
    overflowY: 'auto',
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    marginTop: theme.spacing(2),
  },
  siteTable: {
    width: '100%',
    '& .MuiTableCell-root': {
      padding: theme.spacing(1.5, 2),
      fontSize: '0.875rem',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[50],
      color: theme.palette.text.primary,
    },
  },
  captainIdCell: {
    width: '140px',
  },
  urlCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actionCell: {
    width: '80px',
    textAlign: 'center',
  },
  removeButton: {
    color: theme.palette.error.main,
  },
}));

interface ScheduleConflictDialogProps {
  open: boolean;
  onClose: () => void;
  conflictingSites: SelectedSite[];
  onRemoveSite: (siteId: string) => void;
  timezone?: string;
  timezoneLabel?: string;
}

const ScheduleConflictDialog: React.FC<ScheduleConflictDialogProps> = ({
  open,
  onClose,
  conflictingSites,
  onRemoveSite,
  timezone,
  timezoneLabel,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const formatWindowDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(new Date(dateString));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle onClose={onClose} closeLabel={t('global.close')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Warning color="error" />
          {t('cohorts.validation.downtimeConflictTitle')}
        </div>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph>
          {t('cohorts.validation.downtimeOutsidePreferredRange')}
        </Typography>
        <Paper variant="outlined" className={classes.siteTableContainer}>
          <Table className={classes.siteTable} size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('cohorts.captainId')}</TableCell>
                <TableCell>{t('cohorts.learnUrl')}</TableCell>
                <TableCell>
                  {t('cohorts.preferredWindow')}
                  {timezoneLabel ? ` (${timezoneLabel})` : ''}
                </TableCell>
                <TableCell className={classes.actionCell}>{t('cohorts.removeSite')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conflictingSites.map((site) => (
                <TableRow key={site.captain_id}>
                  <TableCell className={classes.captainIdCell}>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {site.captain_id}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.urlCell}>
                    <Typography variant="body2" color="textSecondary">
                      {site.learn_url || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      {formatWindowDate(site.notifications?.start_suggested_date)}
                    </Typography>
                    {site.notifications?.start_suggested_date !==
                      site.notifications?.end_suggested_date && (
                      <>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {t('global.to')}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {formatWindowDate(site.notifications?.end_suggested_date)}
                        </Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell className={classes.actionCell}>
                    <IconButton
                      aria-label={t('global.remove')}
                      onClick={() => onRemoveSite(site.captain_id)}
                      className={classes.removeButton}
                      size="small"
                    >
                      <Clear />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleConflictDialog;
